import {
    Button,
    Form,
    FormLayout,
    Frame,
    Layout,
    Page,
    TextField,
    LegacyCard,
    PageActions,
    Banner,
    DescriptionList,
    Thumbnail,
    List,
    Text,
    Checkbox
} from "@shopify/polaris";
import { useCallback, useEffect, useState } from "react";
import { cloneDeep } from 'lodash';
import { useHistory, useParams } from 'react-router-dom';
import Api from '../../helpers/api';
import debounce from 'lodash.debounce';
import CustomerOnHold from "../customers/CustomerOnHold";
import jwtDecode from "jwt-decode";
import * as helpers from '../orders/helpers';

const PriceMarker = ({ price, strikethrough }) => {
    const style = strikethrough
        ? { textDecoration: 'line-through' }
        : {};

    return (
        <Text variant="headingMd" as="h2">
            <span style={style}>${price}</span>
        </Text>
    );
}

const FinalPrice = ({ lineItem, children }) => {
    const [showInput, setShowInput] = useState(false);
    const onClick = useCallback(() => {
        setShowInput(true);
    }, []);

    const prices = Array
        .from(new Set([
            parseFloat(lineItem.price.unit || '0.00').toFixed(2),
        ]))
        .map((price, i, list) => ({
            price,
            strikethrough: i == 0 && list.length > 1,
        }));

    if (! showInput) {
        const style = { cursor: 'pointer' };

        return (
            <div onClick={onClick} title="Enter custom price" style={style}>
                {prices.map((props, i) => <PriceMarker {...props} key={i} />)}
            </div>
        );
    }

    return (
        <>
            {children}
        </>
    );
}

const Refunds = () => {
    const history = useHistory();
    const {customerid} = useParams();
    const [adminUserId, setAdminUserId] = useState(null);

    const [customer, setCustomer] = useState(null);
    const [lineItems, setLineItems] = useState([]);

    const [searchInput, setSearchInput] = useState('');
    const [error, setError] = useState(false);
    const [isSearching, setSearching] = useState(false);
    const [errors, setErrors] = useState({});
    const [hasMoreResults, setHasMoreResults] = useState(false);
    const [creating, setCreating] = useState(false);

    const [totalPrice, setTotalPrice] = useState(0);

    const [isPlaceOrderVisible, setIsPlaceOrderVisible] = useState(false);

    const [generateGiftCard, setGenerateGiftCard] = useState(true);
    const [giftCardCode, setGiftCardCode] = useState('');
    const [refundResponse, setRefundResponse] = useState(null);

    useEffect(() => {
        if (lineItems.some(item => parseInt(item.quantity) > 0)) {
            setIsPlaceOrderVisible(true);
        } else {
            setIsPlaceOrderVisible(false);
        }
    }, [lineItems]);

    //This is trick that Rado provided, ask him, I don't know how it works :)
    //This function basically rounds up/down quantity to closest step
    const debounceQuantityUpdate = useCallback(debounce((key, quantity) => {
        setLineItems((lineItems) => {
            let clone = cloneDeep(lineItems);

            if (lineItems[key].increment_quantity_by)
            {
                const step = clone[key].increment_quantity_by;

                if (quantity > 0 && quantity < step) {
                    quantity = step;
                } else if ((quantity % step) < (step / 2)) {
                    quantity = quantity - (quantity % step);
                } else {
                    quantity = parseInt(quantity) + parseInt(step - (quantity % step));
                }

                clone[key].quantity = quantity.toString();
                clone[key].price.total = helpers.calculateTotalPrice(clone[key]).toFixed(2);
            }

            return clone;
        });

    }, 500), [lineItems]);

    const updateQuantity = useCallback((key, quantity) => {
        if (quantity == '') {
            quantity = 0;
        }

        quantity = parseInt(quantity);

        if (lineItems[key].inventory_quantity < quantity) {
            return;
        }

        let clone = cloneDeep(lineItems);

        clone[key].quantity = quantity.toString();
        clone[key].price.total = helpers.calculateTotalPrice(clone[key]).toFixed(2);

        setLineItems(clone);

        const totalPrice = clone.reduce((acc, item) => {
            return acc + parseFloat(item.price.total);
        }, 0.0);
        setTotalPrice(totalPrice);

        debounceQuantityUpdate(key, quantity);
    }, [lineItems]);

    const updateManualPrice = useCallback((key, price) => {
        let clone = cloneDeep(lineItems);

        if (price < 0) {
            price = 0;
        } else if (parseFloat(price) > parseFloat(clone[key].price.unit)) {
            price = clone[key].price.unit;
        }

        clone[key].price.manual = price.toString();
        clone[key].price.total = helpers.calculateTotalPrice(clone[key]).toFixed(2);

        setLineItems(clone);

        const totalPrice = clone.reduce((acc, item) => {
            return acc + parseFloat(item.price.total);
        }, 0.0);
        setTotalPrice(totalPrice);

    }, [lineItems]);

    // Getting the customer from the router parameter.
    useEffect(() => {
        shopify.idToken().then(sessionToken => {
            const decodedToken = jwtDecode(sessionToken);

            if (decodedToken['sub']) {
                const id = decodedToken['sub'];
                setAdminUserId(id);
            }
        });

        Api.getCustomer(customerid).then( response => {
            setCustomer(response.data.data);
        });

    }, []);

    const searchProducts = async (event) => {
        event.preventDefault();

        if ((searchInput.length > 0 && searchInput.length < 3) || searchInput.length == 0) {
            setError(true);
            return;
        }

        setRefundResponse(null);
        setError(false);
        setErrors({});
        setSearching(true);

        const { data } = await Api.getProducts(searchInput, false, 1)
        const selectedLineItems = [...lineItems].filter(item => parseInt(item.quantity, 10) > 0)

        setHasMoreResults(data.meta.last_page > 1);

        const updatedLineItems = data.data.filter(item => ! selectedLineItems.some(({ id }) => id == item.id))
            .map(variant => ({
                id: variant.id,
                shopify_product_id: variant.shopify_product_id,
                quantity: "0",
                title: `${variant.sku} - ${variant.product_title}`,
                price: {
                    unit: variant.price,
                    discounted: variant.price,
                    total: 0,
                    manual: null,
                },
                image_url: variant.image_url,
                inventory_quantity: variant.inventory_quantity,
                options: variant.options,
                increment_quantity_by : variant.increment_quantity_by,
            })).concat(selectedLineItems);

        setLineItems(updatedLineItems);
        setSearching(false);
    }

    const placeRefund = async () => {
        setErrors({});

        const submitErrors = {};

        const variants = lineItems
            .filter(item => item.quantity > 0)
            .map(item => ({
                id: item.id,
                quantity: item.quantity,
                custom_attributes: item.custom_attributes,
                product_id: item.shopify_product_id,
                original_price: parseFloat(item.price.unit),
                manual_price: parseFloat(item.price.manual),
            }));

        const draftOrderData = {
            variants,
            total_price: lineItems.reduce((acc, item) => {
                return acc + parseFloat(item.price.total);
            }, 0.0),
            user_id: adminUserId,
            customer_id: customer.id,
            generates_gift_card: generateGiftCard,
            gift_card_code: giftCardCode,
        };

        try {
            let { data } = await Api.placeRefund(draftOrderData);

            return data.data;

        } catch (error) {
            if (error.response.status == 422) {
                Object.keys(error.response.data.errors).forEach(key => {
                    submitErrors[key] = error.response.data.errors[key][0];
                });
                setErrors(submitErrors);
            } else {
                setCreating(false);
                throw error;
            }
        }

        return null;
    };

    const placeAndReview = async () => {
        setCreating(true);

        const response = await placeRefund();

        if (response) {
            setLineItems([]);

            setRefundResponse(response)
        }

        setCreating(false);
    }

    return (
        <Frame>
            <Page title={`Add Refund`} backAction={{ content: "Customer", onAction: () => {history.push(`/customers/${customerid}`)} }}>
                <Layout>
                    <CustomerOnHold customer={customer}/>

                    <Layout.Section>
                    <LegacyCard title={`Customer Account`} sectioned>
                        <DescriptionList items={[
                            {term: 'Name', description: `${customer?.additional_data.first_name} ${customer?.additional_data.last_name}` },
                            {term: 'Email', description: customer?.email },
                            {term: 'Phone', description: customer?.phone_number },
                        ]} />
                    </LegacyCard>
                    </Layout.Section>
                    <Layout.Section>
                        <LegacyCard title="Line Items">
                            <LegacyCard.Section>
                                <Form onSubmit={searchProducts}>
                                    <FormLayout>
                                            <TextField label="Search" value={searchInput} onChange={setSearchInput} />
                                            {error && (
                                                <Text tone="critical">
                                                    Search must have at least 3 characters.
                                                </Text>
                                            )}
                                            <Button  submit loading={isSearching} variant="primary">Search</Button>
                                    </FormLayout>
                                </Form>
                            </LegacyCard.Section>
                            {lineItems.length > 0 && (
                                <>
                                    <LegacyCard.Section title="Line Items">
                                        <table className="Polaris-DataTable__Table" style={{width: '100%'}}>
                                            <thead>
                                                <tr>
                                                    <th className="Polaris-DataTable__Cell" colSpan="2" style={{ textAlign: 'left'}}>Item</th>
                                                    <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left',}}>Stock</th>
                                                    <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left', width: '80px' }}>Quantity</th>
                                                    <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left'}}>Unit Price</th>
                                                    <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left'}}>Final Price</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                            {lineItems.map( (line_item, key) => {
                                                    return (
                                                        <tr key={line_item.id}>
                                                            <td className="Polaris-DataTable__Cell"><Thumbnail source={line_item.image_url} /></td>
                                                            <td className="Polaris-DataTable__Cell" style={{whiteSpace: 'normal'}}>
                                                                <div>
                                                                    {line_item.title}
                                                                    {line_item.options && line_item.options.length > 0 ? ' - ' : null}
                                                                    {line_item.options ? line_item.options.map((option) => ' ' + option.value + ' ') : null}
                                                                </div>
                                                            </td>
                                                            <td className="Polaris-DataTable__Cell">
                                                                <Text variant="headingMd" as="h2">{line_item.inventory_quantity}</Text>
                                                            </td>
                                                            <td className="Polaris-DataTable__Cell">
                                                                <TextField value={line_item.quantity}
                                                                           min={0}
                                                                           max={line_item.inventory_quantity}
                                                                           step={line_item.increment_quantity_by ?? 1}
                                                                           type="number"
                                                                           onChange={quantity => updateQuantity(key, quantity)}
                                                                />
                                                            </td>
                                                            <td className="Polaris-DataTable__Cell">
                                                                <FinalPrice lineItem={line_item}>
                                                                    <TextField type="number"
                                                                               prefix="$"
                                                                               value={line_item.price.manual}
                                                                               min="0"
                                                                               max={line_item.price.unit}
                                                                               onChange={price => updateManualPrice(key, price)}/>
                                                                </FinalPrice>
                                                            </td>
                                                            <td className="Polaris-DataTable__Cell">
                                                                <Text variant="headingMd" as="h2">${parseFloat(line_item.price.total).toFixed(2)}</Text>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td className="Polaris-DataTable__Cell" colSpan="4">
                                                        <Text variant="headingMd" as="h2">
                                                            Total
                                                        </Text>
                                                    </td>
                                                    <td className="Polaris-DataTable__Cell">
                                                        <Text variant="headingMd" as="h2">
                                                            {`$${totalPrice.toFixed(2)}`}
                                                        </Text>
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </LegacyCard.Section>
                                </>
                            )}
                            {hasMoreResults && (
                                <LegacyCard.Section>
                                    <Banner tone="info">
                                        <p>There are more matching products. Please narrow down the search query.</p>
                                    </Banner>
                                </LegacyCard.Section>
                            )}
                        </LegacyCard>
                    </Layout.Section>
                    {lineItems.length > 0 && (<Layout.Section><LegacyCard title="Gift Card" sectioned>
                        <FormLayout>
                            <Checkbox
                                label="Generate Gift Card"
                                checked={generateGiftCard}
                                onChange={(checked) => setGenerateGiftCard(checked)}
                            />
                            {generateGiftCard && (
                                <TextField
                                    label="Gift Card Code (optional)"
                                    value={giftCardCode}
                                    onChange={setGiftCardCode}
                                    placeholder="Leave empty to auto-generate"
                                />
                            )}
                        </FormLayout>
                    </LegacyCard></Layout.Section>)}
                    {refundResponse && (
                        <Layout.Section>
                            <Banner title="Refund Generated Successfully" tone="success">
                                <p>The refund has been successfully processed.</p>
                                {refundResponse.generated_code && (
                                    <p>
                                        <strong>Generated Gift Card Code:</strong> {refundResponse.generated_code}
                                    </p>
                                )}
                            </Banner>
                        </Layout.Section>
                    )}
                    { Object.keys(errors).length > 0 && (
                        <Layout.Section>
                        <Banner
                            title="There were some errors creating the order."
                            tone="critical"
                            >
                                <List>
                                    {Object.keys(errors).map(key => <List.Item><strong>{key.replace('_', ' ')}</strong>: {errors[key]}</List.Item>)}
                                </List>
                            </Banner>
                        </Layout.Section>
                    )}

                    <CustomerOnHold customer={customer}/>
                </Layout>
                <PageActions
                    primaryAction={
                        isPlaceOrderVisible
                            ? {
                                content: 'Place Refund',
                                onAction: placeAndReview,
                                loading: creating,
                            }
                            : undefined
                    }
                />
            </Page>
        </Frame>
    );
}

export default Refunds;
